/**
 * Shared field utilities for data grid columns
 * Contains common render functions and utilities used across all grids
 */

import React from 'react';
import { Chip } from '@mui/material';

/**
 * Render function for summary cells in tree/grouped views
 * Displays "varied (n)" chip when values differ, otherwise shows the single value
 */
export const renderSummaryCell = (params, context) => {
  const theme = context?.theme;
  if (params.value?.type === 'varied') {
    return (
      <Chip
        label={`varied (${params.value.count})`}
        size="small"
        variant="outlined"
        sx={{ 
          height: theme?.dimensions?.chip?.tiny,
          fontSize: theme?.typography?.fontSize?.xs,
          borderColor: theme?.palette?.grey?.[400]
        }}
      />
    );
  }
  // Return plain value without Typography wrapper to match regular columns
  return params.value;
};

/**
 * Create a column definition from a base field and overrides
 * Merges base field properties with grid-specific overrides
 */
export const createColumnDef = (baseField, overrides = {}, theme = null) => {
  const column = {
    field: baseField.field || baseField.id,
    headerName: baseField.headerName,
    resizable: true,  // Enable column resizing by default
    ...baseField,
    ...overrides
  };
  
  // Apply theme-based width if it's a function
  if (typeof column.width === 'function') {
    column.width = column.width(theme);
  }
  
  // Remove properties that shouldn't be in final column def
  delete column.id;
  delete column.patternField;
  delete column.apiField;
  delete column.inGrid;
  delete column.inPattern;
  delete column.searchable;
  delete column.inSuggestions;
  delete column.needsSummary;
  delete column.entryPath;
  delete column.gridOverrides;
  delete column.grids;
  
  return column;
};

/**
 * Get default column visibility model from column definitions
 * Returns an object with field names as keys and visibility as values
 */
export const getDefaultVisibility = (columns) => {
  return columns.reduce((acc, col) => {
    if (col.defaultVisible === false) {
      acc[col.field] = false;
    }
    return acc;
  }, {});
};

/**
 * Filter columns by a set of field names
 * Useful for creating custom column sets from a master list
 */
export const filterColumns = (columns, fieldNames) => {
  return columns.filter(col => fieldNames.includes(col.field));
};

/**
 * Reorder columns based on a field name array
 * Fields not in the order array are appended at the end
 */
export const reorderColumns = (columns, fieldOrder) => {
  const ordered = [];
  const remaining = [...columns];
  
  fieldOrder.forEach(fieldName => {
    const index = remaining.findIndex(col => col.field === fieldName);
    if (index !== -1) {
      ordered.push(remaining.splice(index, 1)[0]);
    }
  });
  
  return [...ordered, ...remaining];
};